import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["hospital", "doctor", "doctorSection"]
  static values = { initialDoctorId: String }

  async connect() {
    const module = await import("tom-select");
    const TomSelect = module.default || module;
    if (!TomSelect) {
      console.error("TomSelect not loaded");
      return;
    }
    this.TomSelect = TomSelect;
    this.initTomSelect();

    const modal = this.element.closest(".modal");
    if (modal) {
      modal.addEventListener("show.bs.modal", (event) => {
        let button = event.relatedTarget;
        if (!button) {
          button = modal;
        }

        const selectedHospitalId = button.getAttribute("data-hospital-id") || button.dataset.hospitalId;
        const selectedDoctorId   = button.getAttribute("data-doctor-id") || button.dataset.doctorId;

        if (selectedHospitalId) {
          const previousHospitalId = this.hospitalTarget.value;
          this.hospitalTarget.value = selectedHospitalId;
          this.initialDoctorIdValue = selectedDoctorId;
          if (previousHospitalId !== selectedHospitalId) {
            // Hospital changed: reload its doctors; the fetch then applies
            // initialDoctorIdValue.
            this.loadDoctors();
          } else if (selectedDoctorId && this.ts) {
            // Same hospital, options already loaded: switch to the new doctor
            // directly. Without this, reopening the modal for a different doctor
            // in the same hospital kept the previously selected doctor.
            this.ts.setValue(selectedDoctorId);
            // If a loadDoctors fetch is still in flight the options aren't there
            // yet and setValue is a no-op; keep initialDoctorIdValue so the
            // fetch applies it on resolve.
            if (String(this.ts.getValue()) === String(selectedDoctorId)) {
              this.initialDoctorIdValue = null;
            }
          }
          this.updateRevisitQuestion();
        }

        const selectedDate = button.getAttribute("data-consult-date");
        const selectedTime = button.getAttribute("data-consult-time");
        const dateInput = document.getElementById("popup_consult_date");
        const timeInput = document.getElementById("popup_consult_time");
        const bannerEl = document.getElementById("popup_selected_slot_banner");
        const bannerTextEl = document.getElementById("popup_selected_slot_text");

        if (selectedDate && selectedTime) {
          if (dateInput) dateInput.value = selectedDate;
          if (timeInput) timeInput.value = selectedTime;
          if (bannerEl && bannerTextEl) {
            const dateFormatted = button.getAttribute("data-consult-date-formatted") || selectedDate;
            bannerTextEl.textContent = `${dateFormatted} at ${selectedTime}`;
            bannerEl.classList.remove("hidden");
          }
        } else {
          if (dateInput) dateInput.value = "";
          if (timeInput) timeInput.value = "";
          if (bannerEl) bannerEl.classList.add("hidden");
        }
      });
    }

    if (this.hospitalTarget.value) {
      this.loadDoctors();
    }
    this.updateRevisitQuestion();
  }

  initTomSelect() {
    this.ts = new this.TomSelect(this.doctorTarget, {
      valueField: "id",
      labelField: "name",
      searchField: ["name", "specialty"],
      placeholder: "Select Doctor",
      allowEmptyOption: false,
      closeAfterSelect: true,
      hideSelected: true,
      render: {
        option: (data, escape) => `
          <div style="display:flex;align-items:center;gap:12px;padding:10px 16px;width:100%;">
            <img src="${escape(data.photo)}" alt="" style="width:42px;height:42px;border-radius:10px;object-fit:cover;object-position:top;flex:none;">
            <div style="min-width:0;line-height:1.3;">
              <div style="font-weight:700;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escape(data.name)}</div>
              <div style="font-size:12px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escape(data.specialty)} &middot; ${escape(data.experience_years)} yrs</div>
            </div>
          </div>`,
        item: (data, escape) => `
          <div style="display:flex;align-items:center;gap:12px;width:100%;">
            <img src="${escape(data.photo)}" alt="" style="width:40px;height:40px;border-radius:10px;object-fit:cover;object-position:top;flex:none;">
            <div style="min-width:0;line-height:1.3;">
              <div style="font-weight:700;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escape(data.name)}</div>
              <div style="font-size:12px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escape(data.specialty)} &middot; ${escape(data.experience_years)} yrs</div>
            </div>
          </div>`
      }
    })
  }

  hospitalChanged() {
    this.loadDoctors()
    this.updateRevisitQuestion()
  }

  // Show/hide the "Have you visited <hospital> before?" question to match the selected
  // hospital (each option carries data-ask-revisit-question), and keep its hospital name
  // in sync. Without this, switching hospitals left the question stale or hidden.
  updateRevisitQuestion() {
    const sel = this.hospitalTarget
    const box = document.getElementById("revisitQuestionPopup")
    if (!sel || !box) return

    const opt = sel.options[sel.selectedIndex]
    const ask = !!opt && opt.getAttribute("data-ask-revisit-question") === "true"

    box.style.display = ask ? "" : "none"

    if (ask) {
      const nameEl = box.querySelector("strong")
      if (nameEl && opt) nameEl.textContent = opt.textContent.trim()
    } else {
      const cb = document.getElementById("registeredPatientPopup")
      if (cb) cb.checked = false
    }
  }

  loadDoctors() {
    const hid = this.hospitalTarget.value
    const section = this.doctorSectionTarget

    requestAnimationFrame(() => section.style.display = "none")

    this.ts.clear(true)
    this.ts.clearOptions()

    if (!hid) return

    fetch(`/hospitals/${hid}/doctors`, { headers: { Accept: "application/json" } })
      .then(r => r.json())
      .then(doctors => {
        if (!doctors.length) return

        requestAnimationFrame(() => {
          section.style.display = "block"

          this.ts.addOptions(doctors.map(d => ({
            id: d.id,
            name: d.name,
            specialty: d.specialty,
            experience_years: d.experience_years,
            photo: d.photo_url
          })))

          this.ts.refreshOptions(false)

          if (this.initialDoctorIdValue) {
            this.ts.setValue(this.initialDoctorIdValue)
            this.initialDoctorIdValue = null
          }
        })
      })
      .catch(err => {
        console.error("Error loading doctors:", err)
        requestAnimationFrame(() => section.style.display = "none")
      })
  }
}
